/**
 * Marketplace SubCategories List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  IconButton,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, ConfirmDialog } from '../../components/common';
import {
  useGetMarketplaceSubCategoriesQuery,
  useDeleteMarketplaceSubCategoryMutation,
  useGetMarketplaceCategoriesQuery,
} from '../../store/api';
import { MarketplaceSubCategory } from '../../types/marketplace';
import moment from 'moment';
import toast from 'react-hot-toast';

export const SubCategoriesListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selected, setSelected] = useState<MarketplaceSubCategory | null>(null);

  const { data, isLoading, error } = useGetMarketplaceSubCategoriesQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    category: categoryFilter ? Number(categoryFilter) : undefined,
  });

  const { data: categoriesData } = useGetMarketplaceCategoriesQuery({ page_size: 100 });

  const [deleteSubCategory, { isLoading: isDeleting }] = useDeleteMarketplaceSubCategoryMutation();

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteSubCategory(selected.id).unwrap();
      toast.success(t('marketplaceCatalog.subcategories.toast.deleted'));
      setDeleteDialog(false);
      setSelected(null);
    } catch (err: any) {
      toast.error(err?.data?.message || t('marketplaceCatalog.subcategories.toast.deleteError'));
    }
  };

  const columns: Column<MarketplaceSubCategory>[] = [
    {
      id: 'icon',
      label: t('marketplaceCatalog.cols.icon'),
      minWidth: 80,
      render: (row) => (
        <Avatar src={row.icon || undefined} alt={row.name} variant="rounded" sx={{ width: 40, height: 40 }}>
          <ImageIcon />
        </Avatar>
      ),
    },
    {
      id: 'name',
      label: t('marketplaceCatalog.cols.name'),
      minWidth: 150,
      render: (row) => <Box sx={{ fontWeight: 600, color: 'primary.main' }}>{row.name}</Box>,
    },
    {
      id: 'name_ar',
      label: t('marketplaceCatalog.cols.nameAr'),
      minWidth: 140,
      render: (row) => <Box dir="rtl">{row.name_ar?.trim() ? row.name_ar : '—'}</Box>,
    },
    {
      id: 'category_name',
      label: t('marketplaceCatalog.cols.category'),
      minWidth: 150,
    },
    {
      id: 'products_count',
      label: t('marketplaceCatalog.cols.products'),
      minWidth: 100,
      align: 'center',
      render: (row) => <Chip label={row.products_count} size="small" color="primary" />,
    },
    {
      id: 'order',
      label: t('marketplaceCatalog.cols.order'),
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'is_active',
      label: t('marketplaceCatalog.cols.status'),
      minWidth: 100,
      render: (row) => (
        <Chip
          label={row.is_active ? t('marketplaceCatalog.status.active') : t('marketplaceCatalog.status.inactive')}
          size="small"
          color={row.is_active ? 'success' : 'default'}
        />
      ),
    },
    {
      id: 'created_at',
      label: t('marketplaceCatalog.cols.created'),
      minWidth: 120,
      render: (row) => moment(row.created_at).format('MMM DD, YYYY'),
    },
    {
      id: 'actions',
      label: t('marketplaceCatalog.cols.actions'),
      minWidth: 120,
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/marketplace/subcategories/${row.id}/edit`)}
            color="primary"
            title={t('marketplaceCatalog.buttons.edit')}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setSelected(row);
              setDeleteDialog(true);
            }}
            color="error"
            title={t('marketplaceCatalog.buttons.delete')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('marketplaceCatalog.subcategories.title')}
        subtitle={t('marketplaceCatalog.subcategories.subtitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.marketplace'), path: '/marketplace/categories' },
          { label: t('nav.subCategories') },
        ]}
        actionLabel={t('marketplaceCatalog.subcategories.add')}
        onAction={() => navigate('/marketplace/subcategories/create')}
      />

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('marketplaceCatalog.subcategories.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('marketplaceCatalog.filters.category')}</InputLabel>
            <Select
              value={categoryFilter}
              label={t('marketplaceCatalog.filters.category')}
              onChange={(e) => {
                setCategoryFilter(e.target.value as number | '');
                setPage(0);
              }}
            >
              <MenuItem value="">{t('marketplaceCatalog.filters.allCategories')}</MenuItem>
              {categoriesData?.results?.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        error={error ? t('marketplaceCatalog.subcategories.loadError') : null}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage={t('marketplaceCatalog.subcategories.empty')}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        title={t('marketplaceCatalog.subcategories.deleteTitle')}
        message={t('marketplaceCatalog.dialog.deleteMessage', { name: selected?.name })}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialog(false);
          setSelected(null);
        }}
        loading={isDeleting}
      />
    </Box>
  );
};

export default SubCategoriesListPage;
